import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X, CheckCircle2 } from 'lucide-react';
import { applyUpdate } from '../utils/pwaUpdate';
import {
  AUTO_UPDATE_AT_KEY, UPDATED_NOTICE_KEY, decideUpdate, isUserBusy, shouldApplyOnNavigation, type UpdateAction,
} from '../utils/autoUpdateModel';
import { probeBusy, watchTyping } from '../utils/pwaAutoUpdate';

/** How often a long-open tab asks the server whether a new version exists. */
const UPDATE_CHECK_MS = 5 * 60_000;

function readNumber(key: string): number | null {
  try {
    const v = sessionStorage.getItem(key);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

/**
 * D-341 (3): a new version refreshes the site by itself - no "approve new version" click.
 * Never in the middle of work (open dialog, typing, a form with unsaved changes): then it waits for the next page
 * transition. The running timer lives on the server and drafts are in the forms' own guards, so nothing is lost.
 * After the refresh a small bar says "עודכן לגרסה חדשה". If the refresh does not take (a stuck service worker), the
 * old manual prompt shows instead of looping.
 */
export default function UpdatePrompt() {
  const { t } = useTranslation();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [updatedBar, setUpdatedBar] = useState(false);
  const [manual, setManual] = useState(false);
  const pendingRef = useRef(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // A tab left open all day still finds out about a new version.
      window.setInterval(() => { void registration.update().catch(() => undefined); }, UPDATE_CHECK_MS);
    },
    onRegisterError(error) {
      console.warn('SW registration failed, clearing stale caches:', error);
      if ('caches' in window) {
        caches.keys().then(names =>
          names.forEach(name => caches.delete(name))
        );
      }
    },
  });

  // After an automatic refresh: the small "updated" bar, a few seconds.
  useEffect(() => {
    watchTyping();
    let flagged = false;
    try {
      flagged = sessionStorage.getItem(UPDATED_NOTICE_KEY) === '1';
      if (flagged) sessionStorage.removeItem(UPDATED_NOTICE_KEY);
    } catch { /* storage blocked: no bar */ }
    if (!flagged) return;
    setUpdatedBar(true);
    const timer = window.setTimeout(() => setUpdatedBar(false), 6000);
    return () => window.clearTimeout(timer);
  }, []);

  const runAutoUpdate = async () => {
    try {
      sessionStorage.setItem(UPDATED_NOTICE_KEY, '1');
      sessionStorage.setItem(AUTO_UPDATE_AT_KEY, String(Date.now()));
    } catch { /* still refresh */ }
    pendingRef.current = false;
    await applyUpdate(updateServiceWorker);
  };

  // A new version was found: refresh now if idle, otherwise at the next page transition.
  useEffect(() => {
    if (!needRefresh) return;
    const last = readNumber(AUTO_UPDATE_AT_KEY);
    const action: UpdateAction = decideUpdate({
      needRefresh,
      busy: isUserBusy(probeBusy()),
      msSinceLastAuto: last === null ? null : Date.now() - last,
    });
    if (action === 'apply') void runAutoUpdate();
    else if (action === 'wait') { pendingRef.current = true; setManual(true); } // busy: only the small "new version · refresh" bar; the auto refresh waits for a clean page transition
    else setManual(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefresh]);

  // The next page transition: apply the waiting update unless a form still holds unsaved changes.
  useEffect(() => {
    if (!pendingRef.current) return;
    if (shouldApplyOnNavigation(probeBusy())) void runAutoUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleUpdate = async () => {
    if (busy) return;
    setBusy(true);
    await applyUpdate(updateServiceWorker);
    // applyUpdate triggers a hard reload, so we don't really get here. Just in
    // case something goes wrong, clear the busy flag.
    setBusy(false);
  };

  return (
    <>
      {updatedBar && (
        <div
          role="status"
          data-testid="updated-bar"
          className="fixed bottom-4 start-4 end-4 md:end-auto md:start-4 z-50 flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-4 py-2 text-sm text-success-700 shadow-elevated"
        >
          <CheckCircle2 size={16} aria-hidden="true" />
          {t('pwa.updatedBar')}
        </div>
      )}
      {needRefresh && manual && (
        <div className="fixed bottom-4 start-4 end-4 md:end-auto md:start-4 md:w-80 z-50 bg-surface-0 border border-surface-300/60 shadow-elevated rounded-lg p-4 flex items-center gap-3">
          <RefreshCw size={20} className={`text-accent-text flex-shrink-0 ${busy ? 'animate-spin' : ''}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-surface-800">{t('pwa.updateAvailable')}</p>
          </div>
          <button
            onClick={() => void handleUpdate()}
            disabled={busy}
            className="btn btn-primary text-xs py-1.5 px-3 flex-shrink-0 disabled:opacity-60"
          >
            {busy ? t('pwa.updating') : t('pwa.update')}
          </button>
          <button
            onClick={() => setNeedRefresh(false)}
            className="text-surface-500 hover:text-surface-600 flex-shrink-0"
            aria-label={t('pwa.dismiss')}
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
}
